/**
 * An optional extra a run can produce (Volumes by chainage, PDF report,
 * GeoTIFF / DXF export, …): a checkbox row whose own fields appear under it
 * while it is on. Owns the two "can't use this" states so every dialog shows
 * them the same way:
 *
 *  - **locked** (`gate` closed): the whole row (checkbox included) is muted,
 *    with `aria-disabled` + `data-locked`, never the HTML `disabled`
 *    attribute — the click must still reach us, because the click is what
 *    opens the upgrade dialog (CLAUDE.md feature gating). A locked add-on
 *    never reads as on, and with the default `revealChildren='checked'` never
 *    shows its body. With `revealChildren='always'` the body stays mounted
 *    and must keep its own controls shut while locked (as
 *    `StationVolumesFields` does via its own gate check).
 *  - **cannot apply** (`disabledReason`): a data condition, not a paywall, so
 *    the plain `disabled` attribute is right, with the reason printed under it.
 *    Locked wins: the upsell has to stay reachable.
 *
 * The dialog still re-reads the gate when it builds the mutation payload — a
 * subscription can lapse while the dialog is open — this is display only.
 */
import { useId, type ReactNode } from 'react';

import { useFeature, type FeatureResult } from '@/hooks/useFeature';
import type { FeatureKey } from '@/lib/featureGates';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';

export interface AddOnToggleProps {
  /** id of the checkbox; the label's `htmlFor` points at it. */
  id: string;
  /** data-testid of the checkbox. The row gets `${testId}-addon`, the reason
   *  `${testId}-reason`, the upgrade badge `${testId}-upgrade`. */
  testId: string;
  label: ReactNode;
  help?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  gate?: FeatureKey;
  /** Set (non-empty) when the add-on cannot apply right now; shown under it. */
  disabledReason?: ReactNode;
  /** `'checked'` (default): the body mounts only while on. `'always'`: the body
   *  is always mounted and decides for itself what to show — for a body that
   *  has something to say while off (e.g. "load alignments from LandXML"). */
  revealChildren?: 'checked' | 'always';
  children?: ReactNode;
}

export function AddOnToggle(props: AddOnToggleProps) {
  return props.gate ? <GatedAddOnToggle {...props} gate={props.gate} /> : <AddOnToggleView {...props} lock={null} />;
}

/** Separate component so `useFeature` is only called when there is a gate —
 *  a hook cannot be called conditionally. */
function GatedAddOnToggle(props: AddOnToggleProps & { gate: FeatureKey }) {
  const feature = useFeature(props.gate);
  return <AddOnToggleView {...props} lock={feature.allowed ? null : feature} />;
}

const present = (n: ReactNode) => n !== null && n !== undefined && n !== false && n !== '';

function AddOnToggleView({
  id,
  testId,
  label,
  help,
  checked,
  onCheckedChange,
  disabledReason,
  revealChildren = 'checked',
  children,
  lock,
}: AddOnToggleProps & { lock: Pick<FeatureResult, 'gate' | 'promptUpgrade'> | null }) {
  const locked = lock !== null;
  const disabled = !locked && present(disabledReason);
  const on = checked && !locked && !disabled;
  const showBody = revealChildren === 'always' || on;
  // The help and the "cannot apply" reason describe the checkbox (§12.5): a
  // screen reader hears why it is disabled, not just that it is.
  const baseId = useId();
  const helpId = `${baseId}-help`;
  const reasonId = `${baseId}-reason`;
  const describedBy = [present(help) && helpId, disabled && reasonId].filter(Boolean).join(' ') || undefined;

  return (
    <div data-testid={`${testId}-addon`} className='flex min-w-0 flex-col gap-1'>
      {/* `items-start` + the box's `mt-0.5`: a label that wraps keeps the box
          on its first line instead of centring it between the lines. */}
      <div data-testid={`${testId}-row`} className={cn('flex items-start gap-2', locked && 'opacity-50')}>
        <Checkbox
          id={id}
          className='mt-0.5'
          data-testid={testId}
          checked={on}
          disabled={disabled}
          aria-disabled={locked ? true : undefined}
          aria-describedby={describedBy}
          data-locked={locked ? 'true' : undefined}
          onCheckedChange={(c) => {
            if (lock) {
              lock.promptUpgrade();
              return;
            }
            onCheckedChange(!!c);
          }}
        />
        <Label htmlFor={id} className={disabled ? 'opacity-60' : undefined}>
          {label}
        </Label>
        {lock && (
          <span data-testid={`${testId}-upgrade`}>
            <UpgradeBadge gate={lock.gate} />
          </span>
        )}
      </div>
      {present(help) && (
        <p id={helpId} className='pl-6 text-xs break-words text-slate-600 dark:text-stone-400'>
          {help}
        </p>
      )}
      {disabled && (
        <p
          id={reasonId}
          data-testid={`${testId}-reason`}
          // Muted, not amber: a condition of the inputs, not a warning.
          className='pl-6 text-xs break-words text-muted-foreground'>
          {disabledReason}
        </p>
      )}
      {showBody && present(children) && <div className='mt-1 flex min-w-0 flex-col gap-3 pl-6'>{children}</div>}
    </div>
  );
}
